/* import __COLOCATED_TEMPLATE__ from './pane.hbs'; */
/* RESPONSIBLE TEAM: team-actions */

import Component from '@glimmer/component';
import { useResource } from 'ember-resources';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import { type ChildAction } from 'embercom/objects/inbox/command-k/action';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type CommandKService from 'embercom/services/command-k';
import type Session from 'embercom/services/session';
import { type CommandKPaneComponentArgs } from 'embercom/lib/inbox2/command-k-types';
import type TriggerProcedureAction from 'embercom/objects/inbox/command-k/trigger-procedure';
import { type TriggerableProcedure } from 'embercom/objects/inbox/command-k/trigger-procedure';
import { request } from 'embercom/lib/inbox/requests';
import { captureException } from 'embercom/lib/sentry';

interface Signature {
  Args: CommandKPaneComponentArgs<TriggerProcedureAction>;
}

export default class TriggerProcedurePaneComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare commandK: CommandKService;

  @tracked query = '';
  @tracked procedures: TriggerableProcedure[] | null = null;

  selection = useResource(this, NavigableSelection, () => ({
    items: this.items,
    isCommandKVisible: this.commandK.isVisible,
  }));

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadProcedures();
  }

  get items(): ChildAction[] {
    let source = this.procedures ?? [];
    let query = this.query.toLowerCase();
    let filtered = source.filter((p) => p.name.toLowerCase().includes(query));
    return this.args.actionItem.buildResults(filtered);
  }

  @action async loadProcedures() {
    try {
      let response = await request(
        `/ember/inbox/conversations/triggerable_procedures?app_id=${this.session.workspace.id}`,
      );
      let data = (await response.json()) as { procedures: TriggerableProcedure[] };
      this.procedures = data.procedures;
    } catch (error: unknown) {
      captureException(error);
      this.procedures = [];
    }
    this.autoSelectFirst();
  }

  @action search(query: string) {
    this.query = query;
    this.autoSelectFirst();
  }

  private autoSelectFirst() {
    if (this.selection.items.length) {
      this.selection.select(this.selection.items[0]);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CommandK::TriggerProcedure::Pane': typeof TriggerProcedurePaneComponent;
    'inbox2/command-k/trigger-procedure/pane': typeof TriggerProcedurePaneComponent;
  }
}
